/**
 * Resolve a configured icon *name* to a lucide component.
 *
 * Nav config (the Frontend Modules configurator, and entity schemas) stores an
 * icon as a string, so rendering it needs a name→component lookup.
 *
 * The lookup reads the name off the lucide namespace directly rather than
 * enumerating it into a map. Two reasons:
 *   - `Object.entries` over the namespace is not dependable. The shared test
 *     setup (`setupLucideIconMocks` in @locus-taxy/encore-test-utils) replaces
 *     lucide-react with a Proxy whose `ownKeys` returns `[]`, so an
 *     enumeration-built map comes out empty under test while direct property
 *     access still works.
 *   - It avoids building a ~2000-entry map at module load.
 *
 * lucide@1.x exports each icon under both `Truck` and `TruckIcon` (the same
 * object), so we try the bare name and then the `Icon` suffix.
 */
import type { ComponentType } from 'react';
import * as LucideNamespace from 'lucide-react';

/** Exports that are PascalCase but are NOT icons. */
const NON_ICON_EXPORTS = new Set(['Icon', 'LucideIcon', 'createLucideIcon']);

type IconComponent = ComponentType<{ className?: string }>;

const lucide = LucideNamespace as unknown as Record<string, unknown>;

function isRenderableComponent(value: unknown): value is IconComponent {
  if (typeof value === 'function') {
    return true;
  }
  // forwardRef components are objects with a `render` function — this is the
  // shape lucide@1.x actually ships.
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { render?: unknown }).render === 'function'
  );
}

/**
 * Look up an icon component by its configured name.
 *
 * Returns `undefined` when the name is absent, is not PascalCase, names a
 * lucide export that isn't an icon, or doesn't exist. The shipped nav config
 * also contains Encore `L*` icon names (`LVehicleModel`, `LDispatcherOrders`)
 * which are NOT lucide; those resolve to `undefined` here so the caller falls
 * back to its own default. Reconciling the two vocabularies is tracked on
 * DSCO-6206.
 */
export function getConfiguredIcon(name?: string): IconComponent | undefined {
  if (!name || !/^[A-Z][A-Za-z0-9]*$/.test(name)) {
    return undefined;
  }
  if (NON_ICON_EXPORTS.has(name)) {
    return undefined;
  }

  const candidate = lucide[name] ?? lucide[`${name}Icon`];
  return isRenderableComponent(candidate) ? candidate : undefined;
}
